import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';

import type { LocationDTO } from '@invenco/common-interface/accounts';

import { Card, CardHeader } from '@/components/core/card';
import {
  DataTable,
  DataTableCardBody,
  DataTableCardTitle,
  useDataTable,
  useDataTableState,
  useRowNavigationCallback,
} from '@/components/data-table';
import { useGateways } from '@/gateways/GatewayProvider';
import { formatDateTime } from '@/shared/helpers';

type Props = {
  locationId?: string;
};

const columnHelper = createColumnHelper<LocationDTO>();

const columns = [
  columnHelper.accessor('accountName', {
    header: 'Account',
  }),
  columnHelper.accessor('name', {
    header: 'Name',
  }),
  columnHelper.accessor('createdAt', {
    header: 'Created',
    cell: ({ getValue }) => formatDateTime(getValue()),
  }),
];

export function ManagedLocationsCard({ locationId }: Props) {
  const { accountsGateway } = useGateways();
  const onClickRow = useRowNavigationCallback<LocationDTO>('/locations');

  const { data: managedLocations = [], isLoading } = useQuery({
    queryKey: ['locations', { warehouseLocationId: locationId }],
    queryFn: ({ signal }) => accountsGateway.getLocations(undefined, { signal }),
    select: ({ items }) => items.filter((location) => location.warehouseLocationId === locationId),
    enabled: Boolean(locationId),
  });

  const { state, onSortingChange } = useDataTableState({
    columns,
    tableId: 'location-managed-locations',
  });

  const table = useDataTable({
    data: managedLocations,
    columns,
    state,
    isLoading,
    manualPagination: true,
    onSortingChange,
  });

  return (
    <Card aria-label="Managed locations">
      <CardHeader>
        <DataTableCardTitle table={table}>Managed locations</DataTableCardTitle>
      </CardHeader>
      <DataTableCardBody table={table}>
        <DataTable table={table} onClickRow={onClickRow} />
      </DataTableCardBody>
    </Card>
  );
}
